//src/pages/personnel/PersonnelRequestsPage.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  LuCalendarDays,
  LuClipboardCheck,
  LuHistory,
  LuPencil,
  LuPlus,
  LuRefreshCw,
  LuSearch,
  LuTruck,
  LuUser,
  LuUsers,
} from "react-icons/lu";

import {
  useTranslation,
} from "react-i18next";

import usePersonnelRequestsData from "../../hooks/usePersonnelRequestsData";
import usePersonnelPermissions from "../../hooks/usePersonnelPermissions";
import PersonnelAbsenceFormModal from "../../components/personnel/PersonnelAbsenceFormModal";
import {
  approvePersonnelAbsence,
  rejectPersonnelAbsence,
  cancelPersonnelAbsence,
  fetchPersonnelAbsenceAudit,
} from "../../api/personnelApi";

import {
  markPersonnelResourceStale,
} from "../../utils/personnelCache";

/* =========================================================
 * CONSTANTS
 * ======================================================= */

const ABSENCE_TYPES = [
  "HOLIDAY",
  "DAY_OFF",
  "SICK_LEAVE",
  "UNPAID_LEAVE",
  "PARENTAL_LEAVE",
  "BEREAVEMENT",
  "TRAINING",
  "OTHER",
];

const STATUS_ORDER = {
  PENDING: 0,
  APPROVED: 1,
  REJECTED: 2,
  CANCELLED: 3,
};

/* =========================================================
 * HELPERS
 * ======================================================= */

const getPersonKey = (
  personType,
  personId
) =>
  `${personType}:${personId}`;

const normalizeText = (
  value
) =>
  String(
    value ||
      ""
  )
    .trim()
    .toLocaleLowerCase();

const isCancelledRequest = (
  error
) =>
  error?.code ===
    "ERR_CANCELED" ||
  error?.name ===
    "CanceledError" ||
  error?.name ===
    "AbortError";

const sameLocalDay = (
  first,
  second
) =>
  first.getFullYear() ===
    second.getFullYear() &&
  first.getMonth() ===
    second.getMonth() &&
  first.getDate() ===
    second.getDate();

const getLocalDaySerial = (
  date
) =>
  Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

const getAbsenceDuration = (
  absence,
  t
) => {
  const start =
    new Date(
      absence.startAt
    );

  const end =
    new Date(
      absence.endAt
    );

  if (
    absence.isFullDay
  ) {
    const difference =
      Math.round(
        (
          getLocalDaySerial(
            end
          ) -
          getLocalDaySerial(
            start
          )
        ) /
          (
            24 *
            60 *
            60 *
            1000
          )
      ) +
      1;

    return `${Math.max(
      1,
      difference
    )}${t(
      "units.day_short"
    )}`;
  }

  const minutes =
    Math.max(
      0,
      Math.round(
        (
          end -
          start
        ) /
          60000
      )
    );

  const hours =
    Math.floor(
      minutes /
        60
    );

  const remainder =
    minutes %
    60;

  if (
    remainder ===
    0
  ) {
    return `${hours}${t(
      "units.hour_short"
    )}`;
  }

  return `${hours}${t(
    "units.hour_short"
  )} ${remainder}${t(
    "units.minute_short"
  )}`;
};

/* =========================================================
 * PAGE
 * ======================================================= */

export default function PersonnelRequestsPage() {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "personnel"
    );

  const {
    people,
    absences,
    loading,
    refreshing,
    error,
    refresh,
  } =
    usePersonnelRequestsData();

  const {
    requestsCreate,
    requestsEdit,
    requestsApprove,
    requestsCancel,
    requestsDelete,
  } =
    usePersonnelPermissions();

  const [
    search,
    setSearch,
  ] =
    useState(
      ""
    );

  const [
    statusFilter,
    setStatusFilter,
  ] =
    useState(
      "ALL"
    );

  const [
    personTypeFilter,
    setPersonTypeFilter,
  ] =
    useState(
      "ALL"
    );

  const [
    absenceTypeFilter,
    setAbsenceTypeFilter,
  ] =
    useState(
      "ALL"
    );

  const [
    personFilter,
    setPersonFilter,
  ] =
    useState(
      "ALL"
    );
  const [
    selectedRequest,
    setSelectedRequest,
  ] =
    useState(
      null
    );

  const [
    requestHistory,
    setRequestHistory,
  ] =
    useState(
      []
    );

  const [
    requestHistoryLoading,
    setRequestHistoryLoading,
  ] =
    useState(
      false
    );

  const [
    requestHistoryError,
    setRequestHistoryError,
  ] =
    useState(
      null
    );

  const requestHistoryAbortRef =
    useRef(
      null
    );

  const [
    pendingAction,
    setPendingAction,
  ] =
    useState(
      null
    );

  const [
    actionLoading,
    setActionLoading,
  ] =
    useState(
      false
    );

  const [
    actionError,
    setActionError,
  ] =
    useState(
      null
    );

  const [
    actionSuccess,
    setActionSuccess,
  ] =
    useState(
      null
    );

  const [
    absenceEditor,
    setAbsenceEditor,
  ] =
    useState(
      null
    );

  useEffect(
    () => () => {
      requestHistoryAbortRef
        .current
        ?.abort();
    },
    []
  );

  const locale =
    String(
      i18n.resolvedLanguage ||
        i18n.language ||
        "en"
    );

  /* =======================================================
   * PEOPLE
   * ===================================================== */

  const personMap =
    useMemo(
      () => {
        const map =
          new Map();

        people.forEach(
          (
            person
          ) => {
            map.set(
              getPersonKey(
                person.personType,
                person.personId
              ),
              person
            );
          }
        );

        return map;
      },
      [
        people,
      ]
    );

  const requesterMap =
    useMemo(
      () => {
        const map =
          new Map();

        people
          .filter(
            (
              person
            ) =>
              person.personType ===
              "USER"
          )
          .forEach(
            (
              person
            ) => {
              map.set(
                Number(
                  person.personId
                ),
                person
              );
            }
          );

        return map;
      },
      [
        people,
      ]
    );

  const availablePeople =
    useMemo(
      () => {
        if (
          personTypeFilter ===
          "ALL"
        ) {
          return people;
        }

        return people.filter(
          (
            person
          ) =>
            person.personType ===
            personTypeFilter
        );
      },
      [
        people,
        personTypeFilter,
      ]
    );

  /* =======================================================
   * RESOLVED ROWS
   * ===================================================== */

  const rows =
    useMemo(
      () =>
        absences.map(
          (
            absence
          ) => {
            const person =
              personMap.get(
                getPersonKey(
                  absence.personType,
                  absence.personId
                )
              );

            const requester =
              requesterMap.get(
                Number(
                  absence.requestedByUserId
                )
              );

            return {
              ...absence,

              person,

              personName:
                person?.displayName ||
                t(
                  "requests.unknown_person"
                ),

              requesterName:
                requester?.displayName ||
                "—",
            };
          }
        ),
      [
        absences,
        personMap,
        requesterMap,
        t,
      ]
    );

  /* =======================================================
   * FILTERS
   * ===================================================== */

  const filteredRows =
    useMemo(
      () => {
        const needle =
          normalizeText(
            search
          );

        return rows
          .filter(
            (
              row
            ) => {
              if (
                statusFilter !==
                  "ALL" &&
                row.status !==
                  statusFilter
              ) {
                return false;
              }

              if (
                personTypeFilter !==
                  "ALL" &&
                row.personType !==
                  personTypeFilter
              ) {
                return false;
              }

              if (
                absenceTypeFilter !==
                  "ALL" &&
                row.absenceType !==
                  absenceTypeFilter
              ) {
                return false;
              }

              if (
                personFilter !==
                "ALL"
              ) {
                if (
                  getPersonKey(
                    row.personType,
                    row.personId
                  ) !==
                  personFilter
                ) {
                  return false;
                }
              }

              if (!needle) {
                return true;
              }

              const haystack =
                [
                  row.personName,
                  row.requesterName,
                  row.reason,
                  row.absenceType,
                  row.status,
                  t(
                    `absence_types.${row.absenceType}`,
                    row.absenceType
                  ),
                  t(
                    `statuses.${row.status}`,
                    row.status
                  ),
                ]
                  .map(
                    normalizeText
                  )
                  .join(
                    " "
                  );

              return haystack.includes(
                needle
              );
            }
          )
          .sort(
            (
              first,
              second
            ) => {
              const firstRank =
                STATUS_ORDER[
                  first.status
                ] ??
                99;

              const secondRank =
                STATUS_ORDER[
                  second.status
                ] ??
                99;

              if (
                firstRank !==
                secondRank
              ) {
                return (
                  firstRank -
                  secondRank
                );
              }

              return (
                new Date(
                  second.createdAt
                ).getTime() -
                new Date(
                  first.createdAt
                ).getTime()
              );
            }
          );
      },
      [
        rows,
        search,
        statusFilter,
        personTypeFilter,
        absenceTypeFilter,
        personFilter,
        t,
      ]
    );

  /* =======================================================
   * STATS
   * ===================================================== */

  const stats =
    useMemo(
      () => ({
        total:
          absences.length,

        pending:
          absences.filter(
            (
              item
            ) =>
              item.status ===
              "PENDING"
          ).length,

        approved:
          absences.filter(
            (
              item
            ) =>
              item.status ===
              "APPROVED"
          ).length,

        closed:
          absences.filter(
            (
              item
            ) =>
              item.status ===
                "REJECTED" ||
              item.status ===
                "CANCELLED"
          ).length,
      }),
      [
        absences,
      ]
    );

  /* =======================================================
   * FORMATTERS
   * ===================================================== */

  const formatDate =
    (
      value
    ) =>
      new Intl.DateTimeFormat(
        locale,
        {
          day:
            "2-digit",

          month:
            "short",

          year:
            "numeric",
        }
      ).format(
        new Date(
          value
        )
      );

  const formatPeriod =
    (
      row
    ) => {
      const start =
        new Date(
          row.startAt
        );

      const end =
        new Date(
          row.endAt
        );

      if (
        sameLocalDay(
          start,
          end
        )
      ) {
        return formatDate(
          start
        );
      }

      return `${formatDate(
        start
      )} → ${formatDate(
        end
      )}`;
    };

  const formatCreatedAt =
    (
      value
    ) =>
      new Intl.DateTimeFormat(
        locale,
        {
          day:
            "2-digit",

          month:
            "short",

          year:
            "numeric",

          hour:
            "2-digit",

          minute:
            "2-digit",
        }
      ).format(
        new Date(
          value
        )
      );

  const formatHistoryValue =
    (
      field,
      value
    ) => {
      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return t(
          "requests.history.empty_value"
        );
      }

      if (
        field ===
        "absenceType"
      ) {
        return t(
          `absence_types.${value}`,
          String(
            value
          )
        );
      }

      if (
        field ===
        "status"
      ) {
        return t(
          `statuses.${value}`,
          String(
            value
          )
        );
      }

      if (
        field ===
          "startAt" ||
        field ===
          "endAt"
      ) {
        return formatCreatedAt(
          value
        );
      }

      if (
        field ===
        "isFullDay"
      ) {
        return value
          ? t(
              "common.yes"
            )
          : t(
              "common.no"
            );
      }

      return String(
        value
      );
    };

  /* =======================================================
   * REQUEST DETAILS / ACTIONS
   * ===================================================== */

  const loadRequestHistory =
    async (
      requestId,
      {
        silent =
          false,
      } = {}
    ) => {
      const safeId =
        Number(
          requestId
        );

      if (
        !Number.isInteger(
          safeId
        ) ||
        safeId <= 0
      ) {
        return;
      }

      requestHistoryAbortRef
        .current
        ?.abort();

      const controller =
        new AbortController();

      requestHistoryAbortRef.current =
        controller;

      if (!silent) {
        setRequestHistoryLoading(
          true
        );
      }

      setRequestHistoryError(
        null
      );

      try {
        const history =
          await fetchPersonnelAbsenceAudit(
            safeId,
            {
              signal:
                controller.signal,
            }
          );

        if (
          controller
            .signal
            .aborted
        ) {
          return;
        }

        setRequestHistory(
          history
        );
      } catch (
        historyError
      ) {
        if (
          isCancelledRequest(
            historyError
          ) ||
          controller
            .signal
            .aborted
        ) {
          return;
        }

        console.error(
          "[PersonnelRequests] history failed:",
          historyError
        );

        setRequestHistoryError(
          t(
            "requests.history.load_failed"
          )
        );
      } finally {
        if (
          requestHistoryAbortRef
            .current ===
          controller
        ) {
          requestHistoryAbortRef.current =
            null;

          if (!silent) {
            setRequestHistoryLoading(
              false
            );
          }
        }
      }
    };

  const openRequest =
    (
      row
    ) => {
      setSelectedRequest(
        row
      );

      setPendingAction(
        null
      );

      setActionError(
        null
      );

      setActionSuccess(
        null
      );

      setRequestHistory(
        []
      );

      setRequestHistoryError(
        null
      );

      void loadRequestHistory(
        row.id
      );
    };

  const closeRequest =
    () => {
      if (
        actionLoading
      ) {
        return;
      }

      requestHistoryAbortRef
        .current
        ?.abort();

      requestHistoryAbortRef.current =
        null;

      setSelectedRequest(
        null
      );

      setPendingAction(
        null
      );

      setActionError(
        null
      );

      setActionSuccess(
        null
      );

      setRequestHistory(
        []
      );

      setRequestHistoryLoading(
        false
      );

      setRequestHistoryError(
        null
      );
    };

  const requestAction =
    (
      action
    ) => {
      setPendingAction(
        action
      );

      setActionError(
        null
      );

      setActionSuccess(
        null
      );
    };

  const performSelectedAction =
    async () => {
      if (
        !selectedRequest ||
        !pendingAction
      ) {
        return;
      }

      const action =
        pendingAction;

      setActionLoading(
        true
      );

      setActionError(
        null
      );

      setActionSuccess(
        null
      );

      try {
        let updated =
          null;

        if (
          action ===
          "approve"
        ) {
          updated =
            await approvePersonnelAbsence(
              selectedRequest.id
            );
        }

        if (
          action ===
          "reject"
        ) {
          updated =
            await rejectPersonnelAbsence(
              selectedRequest.id
            );
        }

        if (
          action ===
          "cancel"
        ) {
          updated =
            await cancelPersonnelAbsence(
              selectedRequest.id
            );
        }

        if (!updated) {
          throw new Error(
            t(
              "requests.actions.no_result"
            )
          );
        }

        /*
         * Update the open modal immediately.
         */
        setSelectedRequest(
          (
            current
          ) => ({
            ...current,
            ...updated,
          })
        );

        /*
         * Calendar has year-scoped absence caches.
         * Mark them stale so the next Calendar visit
         * performs a silent refresh.
         */
        markPersonnelResourceStale(
          "absences"
        );

        /*
         * Refresh Requests and its cache immediately.
         */
        await refresh({
          silent:
            true,
        });

        await loadRequestHistory(
          selectedRequest.id,
          {
            silent:
              true,
          }
        );

        setPendingAction(
          null
        );

        setActionSuccess(
          t(
            `requests.actions.${action}_success`
          )
        );
      } catch (
        requestError
      ) {
        console.error(
          "[PersonnelRequests] action failed:",
          requestError
        );

        const errorCode =
          requestError
            ?.response
            ?.data
            ?.code ||
          null;

        const translatedErrorKey =
          {
            ABSENCE_OPERATIONAL_CONFLICT:
              "requests.actions.operational_conflict",

            ABSENCE_OVERLAP:
              "requests.actions.absence_overlap",
          }[
            errorCode
          ] ||
          null;

        setActionError(
          translatedErrorKey
            ? t(
                translatedErrorKey
              )
            : (
                requestError
                  ?.response
                  ?.data
                  ?.error ||
                requestError
                  ?.message ||
                t(
                  "requests.actions.failed"
                )
              )
        );
      } finally {
        setActionLoading(
          false
        );
      }
    };

  const selectedApproverName =
    selectedRequest
      ?.approvedByUserId
      ? requesterMap.get(
          Number(
            selectedRequest
              .approvedByUserId
          )
        )?.displayName ||
        "—"
      : null;

  const openCreateRequest =
    () => {
      setSelectedRequest(
        null
      );

      setAbsenceEditor({
        mode:
          "create",

        request:
          null,
      });
    };

  const openEditRequest =
    (
      request
    ) => {
      if (
        request.status !==
        "PENDING"
      ) {
        return;
      }

      setSelectedRequest(
        null
      );

      setAbsenceEditor({
        mode:
          "edit",

        request,
      });
    };

  const closeAbsenceEditor =
    () => {
      setAbsenceEditor(
        null
      );
    };

  const handleAbsenceChanged =
    async () => {
      await refresh({
        silent:
          true,
      });
    };

  /* =======================================================
   * RENDER
   * ===================================================== */

  return (
    <section className="personnel-page personnel-requests-page">
      <div className="personnel-requests-stats">
        <div className="personnel-request-stat">
          <LuUsers />

          <div>
            <strong>
              {
                stats.total
              }
            </strong>

            <span>
              {t(
                "requests.stats.total"
              )}
            </span>
          </div>
        </div>

        <div className="personnel-request-stat is-pending">
          <LuClipboardCheck />

          <div>
            <strong>
              {
                stats.pending
              }
            </strong>

            <span>
              {t(
                "requests.stats.pending"
              )}
            </span>
          </div>
        </div>

        <div className="personnel-request-stat is-approved">
          <LuCalendarDays />

          <div>
            <strong>
              {
                stats.approved
              }
            </strong>

            <span>
              {t(
                "requests.stats.approved"
              )}
            </span>
          </div>
        </div>

        <div className="personnel-request-stat is-closed">
          <LuClipboardCheck />

          <div>
            <strong>
              {
                stats.closed
              }
            </strong>

            <span>
              {t(
                "requests.stats.closed"
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="personnel-requests-toolbar">
        <div className="personnel-requests-search">
          <LuSearch />

          <input
            type="text"
            value={
              search
            }
            onChange={(
              event
            ) =>
              setSearch(
                event.target
                  .value
              )
            }
            placeholder={t(
              "requests.search_placeholder"
            )}
          />
        </div>

        <div className="personnel-requests-filters">
          <select
            value={
              statusFilter
            }
            onChange={(
              event
            ) =>
              setStatusFilter(
                event.target
                  .value
              )
            }
          >
            <option value="ALL">
              {t(
                "requests.filters.all_statuses"
              )}
            </option>

            <option value="PENDING">
              {t(
                "statuses.PENDING"
              )}
            </option>

            <option value="APPROVED">
              {t(
                "statuses.APPROVED"
              )}
            </option>

            <option value="REJECTED">
              {t(
                "statuses.REJECTED"
              )}
            </option>

            <option value="CANCELLED">
              {t(
                "statuses.CANCELLED"
              )}
            </option>
          </select>

          <select
            value={
              personTypeFilter
            }
            onChange={(
              event
            ) => {
              setPersonTypeFilter(
                event.target
                  .value
              );

              setPersonFilter(
                "ALL"
              );
            }}
          >
            <option value="ALL">
              {t(
                "requests.filters.everyone"
              )}
            </option>

            <option value="USER">
              {t(
                "requests.filters.users"
              )}
            </option>

            <option value="DRIVER">
              {t(
                "requests.filters.drivers"
              )}
            </option>
          </select>

          <select
            value={
              absenceTypeFilter
            }
            onChange={(
              event
            ) =>
              setAbsenceTypeFilter(
                event.target
                  .value
              )
            }
          >
            <option value="ALL">
              {t(
                "requests.filters.all_absence_types"
              )}
            </option>

            {ABSENCE_TYPES.map(
              (
                type
              ) => (
                <option
                  key={
                    type
                  }
                  value={
                    type
                  }
                >
                  {t(
                    `absence_types.${type}`,
                    type
                  )}
                </option>
              )
            )}
          </select>

          <select
            value={
              personFilter
            }
            onChange={(
              event
            ) =>
              setPersonFilter(
                event.target
                  .value
              )
            }
          >
            <option value="ALL">
              {t(
                "requests.filters.all_people"
              )}
            </option>

            {availablePeople.map(
              (
                person
              ) => (
                <option
                  key={getPersonKey(
                    person.personType,
                    person.personId
                  )}
                  value={getPersonKey(
                    person.personType,
                    person.personId
                  )}
                >
                  {
                    person.displayName
                  }
                </option>
              )
            )}
          </select>

          <div className="personnel-toolbar-actions">
            {requestsCreate && (
              <button
                type="button"
                className="personnel-new-request-btn"
                onClick={
                  openCreateRequest
                }
              >
                <LuPlus />

                <span>
                  {t(
                    "requests.form.new_request"
                  )}
                </span>
              </button>
            )}

            <button
              type="button"
              className="personnel-refresh-btn"
              onClick={() =>
                refresh({
                  silent:
                    true,
                })
              }
              disabled={
                refreshing
              }
            >
              <LuRefreshCw
                className={
                  refreshing
                    ? "is-spinning"
                    : ""
                }
              />

              <span>
                {t(
                  "requests.refresh"
                )}
              </span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="personnel-calendar-warning">
          {t(
            "requests.refresh_warning"
          )}
        </div>
      )}

      <div className="personnel-requests-table-card">
        <div className="personnel-requests-table-header">
          <div>
            {t(
              "requests.columns.person"
            )}
          </div>

          <div>
            {t(
              "requests.columns.absence"
            )}
          </div>

          <div>
            {t(
              "requests.columns.period"
            )}
          </div>

          <div>
            {t(
              "requests.columns.status"
            )}
          </div>

          <div>
            {t(
              "requests.columns.requested_by"
            )}
          </div>

          <div>
            {t(
              "requests.columns.requested_at"
            )}
          </div>
        </div>

        {loading &&
        absences.length ===
          0 ? (
          Array.from({
            length: 4,
          }).map(
            (
              _,
              index
            ) => (
              <div
                className="personnel-request-row personnel-request-row--skeleton"
                key={
                  index
                }
              >
                {Array.from({
                  length: 6,
                }).map(
                  (
                    __,
                    cellIndex
                  ) => (
                    <div
                      key={
                        cellIndex
                      }
                    >
                      <span className="personnel-request-skeleton" />
                    </div>
                  )
                )}
              </div>
            )
          )
        ) : filteredRows.length ===
          0 ? (
          <div className="personnel-requests-empty">
            <LuClipboardCheck />

            <strong>
              {t(
                "requests.empty.title"
              )}
            </strong>

            <span>
              {t(
                "requests.empty.subtitle"
              )}
            </span>
          </div>
        ) : (
          filteredRows.map(
            (
              row
            ) => (
              <div
                className="personnel-request-row personnel-request-row--clickable"
                key={
                  row.id
                }
                role="button"
                tabIndex={0}
                onClick={() =>
                  openRequest(
                    row
                  )
                }
                onKeyDown={(
                  event
                ) => {
                  if (
                    event.key ===
                      "Enter" ||
                    event.key ===
                      " "
                  ) {
                    event.preventDefault();

                    openRequest(
                      row
                    );
                  }
                }}
              >
                <div className="personnel-request-person">
                  <span className="personnel-request-person-icon">
                    {row.personType ===
                    "DRIVER"
                      ? (
                        <LuTruck />
                      )
                      : (
                        <LuUser />
                      )}
                  </span>

                  <div>
                    <strong>
                      {
                        row.personName
                      }
                    </strong>

                    <span>
                      {row.personType ===
                      "DRIVER"
                        ? t(
                            "requests.filters.drivers"
                          )
                        : t(
                            "requests.filters.users"
                          )}
                    </span>
                  </div>
                </div>

                <div className="personnel-request-type">
                  <strong>
                    {t(
                      `absence_types.${row.absenceType}`,
                      row.absenceType
                    )}
                  </strong>

                  {row.reason && (
                    <span
                      title={
                        row.reason
                      }
                    >
                      {
                        row.reason
                      }
                    </span>
                  )}
                </div>

                <div className="personnel-request-period">
                  <strong>
                    {formatPeriod(
                      row
                    )}
                  </strong>

                  <span>
                    {getAbsenceDuration(
                      row,
                      t
                    )}
                  </span>
                </div>

                <div>
                  <span
                    className={[
                      "personnel-status-badge",

                      `is-${String(
                        row.status
                      ).toLowerCase()}`,
                    ].join(
                      " "
                    )}
                  >
                    {t(
                      `statuses.${row.status}`,
                      row.status
                    )}
                  </span>
                </div>

                <div className="personnel-request-requester">
                  <strong>
                    {
                      row.requesterName
                    }
                  </strong>
                </div>

                <div className="personnel-request-created">
                  {formatCreatedAt(
                    row.createdAt
                  )}
                </div>
              </div>
            )
          )
        )}
      </div>

      <div className="personnel-requests-footer">
        {t(
          "requests.showing",
          {
            shown:
              filteredRows.length,

            total:
              absences.length,
          }
        )}
      </div>

      {selectedRequest && (
        <div
          className="personnel-request-overlay"
          onMouseDown={(
            event
          ) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              closeRequest();
            }
          }}
        >
          <div
            className="personnel-request-modal"
            role="dialog"
            aria-modal="true"
          >
            <div className="personnel-request-modal-header">
              <div>
                <span className="personnel-request-modal-kicker">
                  {t(
                    "requests.details.title"
                  )}
                </span>

                <h3>
                  {
                    selectedRequest
                      .personName
                  }
                </h3>
              </div>

              <button
                type="button"
                className="personnel-request-modal-close"
                onClick={
                  closeRequest
                }
                disabled={
                  actionLoading
                }
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="personnel-request-modal-summary">
              <span className="personnel-request-modal-person-icon">
                {selectedRequest
                  .personType ===
                "DRIVER"
                  ? (
                    <LuTruck />
                  )
                  : (
                    <LuUser />
                  )}
              </span>

              <div>
                <strong>
                  {t(
                    `absence_types.${selectedRequest.absenceType}`,
                    selectedRequest.absenceType
                  )}
                </strong>

                <span>
                  {selectedRequest.personType ===
                  "DRIVER"
                    ? t(
                        "requests.filters.drivers"
                      )
                    : t(
                        "requests.filters.users"
                      )}
                </span>
              </div>

              <span
                className={[
                  "personnel-status-badge",

                  `is-${String(
                    selectedRequest
                      .status
                  ).toLowerCase()}`,
                ].join(
                  " "
                )}
              >
                {t(
                  `statuses.${selectedRequest.status}`,
                  selectedRequest.status
                )}
              </span>
            </div>

            <div className="personnel-request-modal-details">
              <div className="personnel-request-detail-row">
                <span>
                  {t(
                    "requests.details.period"
                  )}
                </span>

                <strong>
                  {formatPeriod(
                    selectedRequest
                  )}
                </strong>
              </div>

              <div className="personnel-request-detail-row">
                <span>
                  {t(
                    "requests.details.duration"
                  )}
                </span>

                <strong>
                  {getAbsenceDuration(
                    selectedRequest,
                    t
                  )}
                </strong>
              </div>

              <div className="personnel-request-detail-row">
                <span>
                  {t(
                    "requests.details.requested_by"
                  )}
                </span>

                <strong>
                  {
                    selectedRequest
                      .requesterName
                  }
                </strong>
              </div>

              <div className="personnel-request-detail-row">
                <span>
                  {t(
                    "requests.details.created_at"
                  )}
                </span>

                <strong>
                  {formatCreatedAt(
                    selectedRequest
                      .createdAt
                  )}
                </strong>
              </div>

              {selectedRequest
                .approvedByUserId && (
                <div className="personnel-request-detail-row">
                  <span>
                    {t(
                      "requests.details.approved_by"
                    )}
                  </span>

                  <strong>
                    {
                      selectedApproverName
                    }
                  </strong>
                </div>
              )}

              {selectedRequest
                .approvedAt && (
                <div className="personnel-request-detail-row">
                  <span>
                    {t(
                      "requests.details.approved_at"
                    )}
                  </span>

                  <strong>
                    {formatCreatedAt(
                      selectedRequest
                        .approvedAt
                    )}
                  </strong>
                </div>
              )}

              <div className="personnel-request-detail-row">
                <span>
                  {t(
                    "requests.details.reason"
                  )}
                </span>

                <strong>
                  {selectedRequest
                    .reason ||
                    t(
                      "requests.details.no_reason"
                    )}
                </strong>
              </div>

              {selectedRequest
                .privateNotes && (
                <div className="personnel-request-detail-row">
                  <span>
                    {t(
                      "requests.details.private_notes"
                    )}
                  </span>

                  <strong>
                    {
                      selectedRequest
                        .privateNotes
                    }
                  </strong>
                </div>
              )}
            </div>

            <div className="personnel-request-history">
              <div className="personnel-request-history-header">
                <span className="personnel-request-history-icon">
                  <LuHistory />
                </span>

                <div>
                  <strong>
                    {t(
                      "requests.history.title"
                    )}
                  </strong>

                  <span>
                    {t(
                      "requests.history.subtitle"
                    )}
                  </span>
                </div>
              </div>

              {requestHistoryLoading ? (
                <div className="personnel-request-history-state">
                  {t(
                    "requests.history.loading"
                  )}
                </div>
              ) : requestHistoryError ? (
                <div className="personnel-request-history-state is-error">
                  {
                    requestHistoryError
                  }
                </div>
              ) : requestHistory.length ===
                0 ? (
                <div className="personnel-request-history-state">
                  {t(
                    "requests.history.empty"
                  )}
                </div>
              ) : (
                <div className="personnel-request-history-list">
                  {requestHistory.map(
                    (
                      historyItem
                    ) => {
                      const visibleChanges =
                        historyItem.action ===
                        "UPDATE"
                          ? Object.entries(
                              historyItem
                                .changes ||
                                {}
                            )
                          : [];

                      const actorName =
                        historyItem
                          .actorName ||
                        (
                          historyItem
                            .actorType ===
                          "SYSTEM"
                            ? t(
                                "requests.history.system"
                              )
                            : t(
                                "requests.history.unknown_actor"
                              )
                        );

                      return (
                        <div
                          className={[
                            "personnel-request-history-item",

                            `is-${String(
                              historyItem
                                .action ||
                                ""
                            ).toLowerCase()}`,
                          ].join(
                            " "
                          )}
                          key={
                            historyItem.id
                          }
                        >
                          <span className="personnel-request-history-marker" />

                          <div className="personnel-request-history-content">
                            <div className="personnel-request-history-top">
                              <strong>
                                {t(
                                  `requests.history.actions.${historyItem.action}`,
                                  historyItem.action
                                )}
                              </strong>

                              <span>
                                {formatCreatedAt(
                                  historyItem
                                    .createdAt
                                )}
                              </span>
                            </div>

                            <span className="personnel-request-history-actor">
                              {t(
                                "requests.history.by_actor",
                                {
                                  name:
                                    actorName,
                                }
                              )}
                            </span>

                            {visibleChanges.length >
                              0 && (
                              <div className="personnel-request-history-changes">
                                {visibleChanges.map(
                                  (
                                    [
                                      field,
                                      change,
                                    ]
                                  ) => (
                                    <div
                                      className="personnel-request-history-change"
                                      key={
                                        field
                                      }
                                    >
                                      <span>
                                        {t(
                                          `requests.history.fields.${field}`,
                                          field
                                        )}
                                      </span>

                                      <strong>
                                        <em>
                                          {formatHistoryValue(
                                            field,
                                            change
                                              ?.old
                                          )}
                                        </em>

                                        <b>
                                          →
                                        </b>

                                        <em>
                                          {formatHistoryValue(
                                            field,
                                            change
                                              ?.new
                                          )}
                                        </em>
                                      </strong>
                                    </div>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              )}
            </div>

            {actionError && (
              <div className="personnel-request-action-message is-error">
                {
                  actionError
                }
              </div>
            )}

            {actionSuccess && (
              <div className="personnel-request-action-message is-success">
                {
                  actionSuccess
                }
              </div>
            )}

            {pendingAction ? (
              <div className="personnel-request-confirm">
                <p>
                  {t(
                    `requests.actions.confirm_${pendingAction}`
                  )}
                </p>

                <div>
                  <button
                    type="button"
                    className="personnel-request-action-btn is-secondary"
                    onClick={() =>
                      setPendingAction(
                        null
                      )
                    }
                    disabled={
                      actionLoading
                    }
                  >
                    {t(
                      "requests.actions.back"
                    )}
                  </button>

                  <button
                    type="button"
                    className={[
                      "personnel-request-action-btn",

                      `is-${pendingAction}`,
                    ].join(
                      " "
                    )}
                    onClick={
                      performSelectedAction
                    }
                    disabled={
                      actionLoading
                    }
                  >
                    {actionLoading
                      ? t(
                          "requests.actions.working"
                        )
                      : t(
                          "requests.actions.confirm"
                        )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="personnel-request-modal-actions">
                {selectedRequest
                  .status ===
                  "PENDING" && (
                  <>
                    {requestsEdit && (
                      <button
                        type="button"
                        className="personnel-request-action-btn is-secondary personnel-request-edit-btn"
                        onClick={() =>
                          openEditRequest(
                            selectedRequest
                          )
                        }
                      >
                        <LuPencil />

                        {t(
                          "requests.form.edit"
                        )}
                      </button>
                    )}

                    {requestsApprove && (
                      <>
                        <button
                          type="button"
                          className="personnel-request-action-btn is-reject"
                          onClick={() =>
                            requestAction(
                              "reject"
                            )
                          }
                        >
                          {t(
                            "requests.actions.reject"
                          )}
                        </button>

                        <button
                          type="button"
                          className="personnel-request-action-btn is-approve"
                          onClick={() =>
                            requestAction(
                              "approve"
                            )
                          }
                        >
                          {t(
                            "requests.actions.approve"
                          )}
                        </button>
                      </>
                    )}
                  </>
                )}

                {selectedRequest
                  .status ===
                  "APPROVED" &&
                  requestsCancel && (
                  <button
                    type="button"
                    className="personnel-request-action-btn is-cancel"
                    onClick={() =>
                      requestAction(
                        "cancel"
                      )
                    }
                  >
                    {t(
                      "requests.actions.cancel"
                    )}
                  </button>
                )}

                {selectedRequest
                  .status !==
                  "PENDING" &&
                  selectedRequest
                    .status !==
                    "APPROVED" && (
                    <button
                      type="button"
                      className="personnel-request-action-btn is-secondary"
                      onClick={
                        closeRequest
                      }
                    >
                      {t(
                        "requests.actions.close"
                      )}
                    </button>
                  )}
              </div>
            )}
          </div>
        </div>
      )}

      {absenceEditor &&
        (
          absenceEditor.mode ===
            "create"
            ? requestsCreate
            : requestsEdit
        ) && (
        <PersonnelAbsenceFormModal
          key={
            absenceEditor
              .request
              ?.id ||
            "new-absence"
          }
          people={
            people
          }
          request={
            absenceEditor
              .request
          }
          canDelete={
            requestsDelete
          }
          onClose={
            closeAbsenceEditor
          }
          onChanged={
            handleAbsenceChanged
          }
        />
      )}
    </section>
  );
}